import { useMemo, useState } from 'react'

import { GuestArrivalActions } from '@/components/pos/GuestArrivalActions'
import { checkInArrivalsForDate } from '@/lib/calendar/checkIn'

/**
 * @param {{
 *   appointments: import('@/types/api').Appointment[]
 *   chairs: import('@/types/api').Chair[]
 *   selectedDate: string
 *   onUpdated?: () => void | Promise<void>
 * }} props
 */
export function CheckInArrivalsPanel({ appointments, chairs, selectedDate, onUpdated }) {
  const arrivals = useMemo(
    () => checkInArrivalsForDate(appointments, selectedDate),
    [appointments, selectedDate],
  )
  const activeChairs = useMemo(
    () => chairs.filter((chair) => chair.is_active !== false),
    [chairs],
  )
  const [error, setError] = useState('')

  if (arrivals.length === 0) {
    return null
  }

  return (
    <section className="card-surface overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-border/60 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Guest arrivals</h2>
          <p className="text-[11px] text-muted-foreground">
            Check in guests, then start service to move them in chair.
          </p>
        </div>
        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          {arrivals.length} waiting
        </span>
      </div>

      {error ? (
        <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-xs text-red-700">{error}</div>
      ) : null}

      <div className="divide-y divide-border/60">
        {arrivals.map((appointment) => (
          <div key={appointment.id} className="px-4 py-3">
            <GuestArrivalActions
              appointment={appointment}
              chairs={activeChairs}
              onUpdated={async () => {
                setError('')
                try {
                  await onUpdated?.()
                } catch (err) {
                  setError(err?.message ?? 'Unable to refresh arrivals.')
                }
              }}
            />
          </div>
        ))}
      </div>
    </section>
  )
}
